import { useCallback, useEffect, useState } from 'react';
import Card from '../../../components/atoms/card';
import YardService, { IYard } from '../../../services/Yard.service';
import { useTranslation } from 'react-i18next';
import Select from '../../../components/atoms/select';
import { EditableChangeEvent } from '../../../utils/types/editable';
import Button from '../../../components/atoms/button';
import ReportsService, {
	MonitoringReport,
	ReportContainerData,
} from '../../../services/Reports.service';
import InputCalendar from '../../../components/molecules/input-calendar';
import Text from '../../../components/atoms/text';
import Table, { TableColumn } from '../../../components/organisms/table';
import { dateObjToStr } from '../../../utils/date';
import SVGIcon from '../../../components/atoms/icons';
import Label from '../../../components/atoms/text/label';
import * as html2pdf from 'html2pdf.js';
import { NotificationToast } from '../../../components/organisms/notification-toast';
import { useRouteLoaderData } from 'react-router-dom';
import { HomeLoaderData } from '../../../types/homeLoaderData';

interface ReportsForm {
	yard: Partial<IYard>;
	period: string;
	type: string;
	format: string;
	date: Date;
	startDate?: Date;
	endDate?: Date;
	month?: string;
	year?: string;
}

const Reports = () => {
	const { t } = useTranslation();
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const reportType = [t('maintenance.title'), t('monitor.title')];
	const reportPeriod = [t('date.daily'), t('date.monthly'), t('date.custom')];

	// Opções de meses
	const months = [
		{ value: '1', label: t('months.january') },
		{ value: '2', label: t('months.february') },
		{ value: '3', label: t('months.march') },
		{ value: '4', label: t('months.april') },
		{ value: '5', label: t('months.may') },
		{ value: '6', label: t('months.june') },
		{ value: '7', label: t('months.july') },
		{ value: '8', label: t('months.august') },
		{ value: '9', label: t('months.september') },
		{ value: '10', label: t('months.october') },
		{ value: '11', label: t('months.november') },
		{ value: '12', label: t('months.december') },
	];

	// Opções de formato para manutenção
	const maintenanceFormats = [t('date.daily'), t('misc.closing')];

	// Opções de formato padrão
	const defaultFormats = [t('reports.complete'), t('reports.error')];

	const [yardsData, setYardsData] = useState<IYard[]>([]);
	const [formData, setFormData] = useState<ReportsForm>({
		yard: {},
		period: '',
		type: '',
		format: '',
		date: new Date(),
		startDate: new Date(),
		endDate: new Date(),
		month: '',
		year: new Date().getFullYear().toString(),
	});

	// Determina quais opções de formato mostrar baseado no tipo selecionado
	const getFormatOptions = () => {
		if (formData.type === t('maintenance.title')) {
			return maintenanceFormats;
		}
		return defaultFormats;
	};

	// Verifica se o botão deve estar desabilitado
	const isDisabled = () => {
		if (!formData.type || !formData.yard.id) return true;

		// Se for manutenção
		if (formData.type === t('maintenance.title')) {
			if (!formData.format) return true;

			// Se for manutenção diário, precisa de data
			if (formData.format === t('date.daily') && !formData.date)
				return true;

			// Se for manutenção fechamento, precisa de mês e ano
			if (
				formData.format === t('misc.closing') &&
				(!formData.month || !formData.year)
			)
				return true;
		}
		// Se for monitoramento
		else if (formData.type === t('monitor.title')) {
			if (!formData.period || !formData.format) return true;

			// Monitoramento com período diário precisa de data
			if (formData.period === t('date.daily') && !formData.date)
				return true;

			// Monitoramento com período mensal precisa de mês e ano
			if (
				formData.period === t('date.monthly') &&
				(!formData.month || !formData.year)
			)
				return true;

			// Monitoramento com período personalizado precisa de data inicial e final
			if (
				formData.period === t('date.custom') &&
				(!formData.startDate || !formData.endDate)
			)
				return true;
		}

		return false;
	};

	const fetchYards = useCallback(async () => {
		const response = await YardService.getAll({
			isActive: 1,
		});
		if (!response) return;
		setYardsData(response.data);
	}, []);

	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	// Reset format quando o tipo muda
	useEffect(() => {
		setFormData((prev) => ({
			...prev,
			format: '',
			period: '',
		}));
	}, [formData.type]);

	const handleYard = (e: EditableChangeEvent) => {
		const newYard = yardsData.find((item) => item.name === e.target.value);
		if (!newYard) return;
		setFormData((prev) => ({
			...prev,
			yard: newYard,
		}));
	};

	const handleInput = (e: EditableChangeEvent) => {
		setFormData((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	};

	const handleDateChange = (date: Date) => {
		handleInput({ target: { name: 'date', value: date } });
	};

	const handleStartDateChange = (date: Date) => {
		setFormData((prev) => ({
			...prev,
			startDate: date,
		}));
	};

	const handleEndDateChange = (date: Date) => {
		setFormData((prev) => ({
			...prev,
			endDate: date,
		}));
	};

	// Função para ajustar a data para o fim do dia (23:59:59.999)
	const setEndOfDay = (date: Date): Date => {
		const endOfDay = new Date(date);
		endOfDay.setHours(23, 59, 59, 999);
		return endOfDay;
	};

	// Função para ajustar a data para o início do dia (00:00:00.000)
	const setStartOfDay = (date: Date): Date => {
		const startOfDay = new Date(date);
		startOfDay.setHours(0, 0, 0, 0);
		return startOfDay;
	};

	const handleReports = async () => {
		sessionStorage.removeItem('report');

		// Validar se temos os dados necessários
		if (!formData.yard.id) {
			NotificationToast.Warn(t('alert.errors.mandatory'));
			return;
		}

		if (!formData.format) {
			NotificationToast.Warn(t('alert.errors.mandatory'));
			return;
		}

		// Calcular datas baseado no tipo e período selecionado
		const dates = calculateDates();
		if (!dates) {
			NotificationToast.Warn(t('alert.errors.mandatory'));
			return;
		}

		const { dateBegin, dateEnd } = dates;

		// Criar parâmetros base
		const params = new URLSearchParams({
			yard: formData.yard.name || '',
			onlyAlert: formData.format === defaultFormats[1] ? '1' : '0',
			dateBegin: dateBegin.toISOString(),
			dateEnd: dateEnd.toISOString(),
			accountId,
			_t: Date.now().toString(),
		});

		// Adicionar parâmetros específicos do tipo
		if (formData.type === t('monitor.title')) {
			params.append('period', formData.period);
			if (formData.format === defaultFormats[0]) {
				params.append('isFinance', '1');
			}
		} else if (formData.type === t('maintenance.title')) {
			params.append('maintenanceFormat', formData.format);
		}

		// Definir URL baseado no tipo
		const baseUrl =
			formData.type === t('maintenance.title')
				? '/reports/maintenance/data'
				: '/reports/data';

		const reportUrl = `${baseUrl}?${params.toString()}`;

		// Abrir em nova aba
		window.open(reportUrl, '_blank');
	};

	// Função auxiliar para calcular datas
	const calculateDates = (): { dateBegin: Date; dateEnd: Date } | null => {
		// Monitoramento
		if (formData.type === t('monitor.title')) {
			if (formData.period === t('date.daily') && formData.date) {
				return {
					dateBegin: setStartOfDay(formData.date),
					dateEnd: setEndOfDay(formData.date),
				};
			}

			if (
				formData.period === t('date.monthly') &&
				formData.month &&
				formData.year
			) {
				const year = parseInt(formData.year);
				const month = parseInt(formData.month) - 1;

				return {
					dateBegin: setStartOfDay(new Date(year, month, 1)),
					dateEnd: setEndOfDay(new Date(year, month + 1, 0)),
				};
			}

			if (
				formData.period === t('date.custom') &&
				formData.startDate &&
				formData.endDate
			) {
				return {
					dateBegin: setStartOfDay(formData.startDate),
					dateEnd: setEndOfDay(formData.endDate),
				};
			}
		}

		// Manutenção
		if (formData.type === t('maintenance.title')) {
			if (formData.format === t('date.daily') && formData.date) {
				return {
					dateBegin: setStartOfDay(formData.date),
					dateEnd: setEndOfDay(formData.date),
				};
			}

			if (
				formData.format === t('misc.closing') &&
				formData.month &&
				formData.year
			) {
				const year = parseInt(formData.year);
				const month = parseInt(formData.month) - 1;

				return {
					dateBegin: setStartOfDay(new Date(year, month, 1)),
					dateEnd: setEndOfDay(new Date(year, month + 1, 0)),
				};
			}
		}

		return null;
	};

	// Verifica se deve mostrar o campo de data
	const shouldShowDateField = () => {
		// Manutenção diário
		if (
			formData.type === t('maintenance.title') &&
			formData.format === t('date.daily')
		) {
			return true;
		}
		// Monitoramento diário
		if (
			formData.type === t('monitor.title') &&
			formData.period === t('date.daily')
		) {
			return true;
		}
		return false;
	};

	// Verifica se deve mostrar os campos de mês/ano
	const shouldShowMonthYearFields = () => {
		// Manutenção fechamento
		if (
			formData.type === t('maintenance.title') &&
			formData.format === t('misc.closing')
		) {
			return true;
		}
		// Monitoramento mensal
		if (
			formData.type === t('monitor.title') &&
			formData.period === t('date.monthly')
		) {
			return true;
		}
		return false;
	};

	// Verifica se deve mostrar os campos de data personalizada
	const shouldShowCustomDateFields = () => {
		return (
			formData.type === t('monitor.title') &&
			formData.period === t('date.custom')
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<Card className="flex flex-wrap p-4 gap-4">
				<Select
					className="w-60 min-w-fit"
					name="yard"
					value={formData.yard?.name || ''}
					options={yardsData.map((yard) => yard.name)}
					onChange={handleYard}
					placeholder={t('port_management.yard')}
				/>

				<Select
					className="w-60 min-w-fit"
					name="type"
					value={formData.type}
					options={reportType}
					onChange={handleInput}
					placeholder={t('reports.type')}
				/>

				<Select
					className="w-60 min-w-fit"
					name="format"
					value={formData.format}
					options={getFormatOptions()}
					onChange={handleInput}
					placeholder={t('reports.format')}
				/>

				{/* Só mostra período se for monitoramento */}
				{formData.type === t('monitor.title') && (
					<Select
						className="w-60 min-w-fit"
						name="period"
						value={formData.period}
						options={reportPeriod}
						onChange={handleInput}
						placeholder={t('date.period')}
					/>
				)}

				{/* Campo de data (para casos diários) */}
				{shouldShowDateField() && (
					<InputCalendar
						date={formData.date}
						onDateChange={handleDateChange}
						placeholder={t('date.title')}
						required
					/>
				)}

				{/* Campos de mês e ano (para casos mensais/fechamento) */}
				{shouldShowMonthYearFields() && (
					<>
						<Select
							className="w-60 min-w-fit"
							name="month"
							value={
								formData.month
									? months.find(
											(m) => m.value === formData.month
										)?.label || ''
									: ''
							}
							options={months.map((m) => m.label)}
							onChange={(e) => {
								const selectedMonth = months.find(
									(m) => m.label === e.target.value
								);
								handleInput({
									target: {
										name: 'month',
										value: selectedMonth?.value || '',
									},
								});
							}}
							placeholder={t('date.month')}
						/>
						<input
							type="number"
							className="w-60 min-w-fit p-2 border rounded"
							name="year"
							value={formData.year}
							onChange={(e) =>
								handleInput({
									target: {
										name: 'year',
										value: e.target.value,
									},
								})
							}
							placeholder={t('date.year')}
							min="2000"
							max="2100"
						/>
					</>
				)}

				{/* Campos de data personalizada (início e fim) */}
				{shouldShowCustomDateFields() && (
					<>
						<InputCalendar
							date={formData.startDate || new Date()}
							onDateChange={handleStartDateChange}
							placeholder={t('date.start_date')}
							required
						/>
						<InputCalendar
							date={formData.endDate || new Date()}
							onDateChange={handleEndDateChange}
							placeholder={t('date.end_date')}
							required
						/>
					</>
				)}

				<Button disabled={isDisabled()} onClick={handleReports}>
					{t('reports.generate')}
				</Button>
			</Card>
		</div>
	);
};

export default Reports;
